/**
 * Created by Ning Jiang on 2021-06-07.
 */

import {WinPresentationController} from "../WinPresentationController";
import {ReelState} from "../../../core/reel/ReelState";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {Serial} from "../../Serial";
import {gsap} from "gsap";
import {SerialStackedWildSymbol} from "../../symbol/SerialStackedWildSymbol";
import {EnhancerXNudgeOverlay} from "../../view/EnhancerXNudgeOverlay";
import {ReelPositionController} from "../../reels/ReelPositionController";
import {SerialSound} from "../../sound/SerialSound";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {Layers, SerialLayers} from "../../view/SerialLayers";

export class WildNudgeController extends WinPresentationController {

    private readonly _nudgeStepDuration:number = 0.3;
    private readonly _nudgeEase:string = `elastic.out(1, 1)`;

    private _hasWin:boolean;
    private _skipped:boolean = false;
    private _startStepAnimations:(gsap.core.Timeline | null)[] = [];
    public enhancerOverlays:(EnhancerXNudgeOverlay | null)[] = [];
    private _enhancerNudgeAnimations:(gsap.core.Timeline | null)[] = [];

    constructor() {
        super("WildNudge", true);

        if(Serial.model.isRestore) {
            // restore enhancer xNudge overlay and xNudge multiplier.
            Serial.model.parsedInitData.evaluatedArea.forEach((reel, reelId) => {
                const symId = reel.indexOf("WXN");
                if(symId < 0) {
                    return;
                }
                const multiplier = Serial.model.parsedInitData.multiplierOnReel[reelId];
                if(symId === 0) {
                    // respin enhancer
                    const overlay = new EnhancerXNudgeOverlay(reelId, symId);
                    SerialLayers.getLayer(Layers.ENHANCER_NUDGE_OVERLAY)?.addChild(overlay);
                    this.enhancerOverlays[reelId] = overlay;
                    overlay.nudgeTo(ReelPositionController.REEL_AREAS[reelId].height, 0.01, "none");
                    overlay.updateMultiplier(multiplier, false);
                    return;
                }
                if(symId === 1) {
                    // scatter enhancer
                    if(!Serial.model.parsedInitData.enhancerReelsOpenedNextSpin[reelId][1]) {
                        debugger;
                        throw new Error(`WildNudge can not restore xNudge in reel ${reelId} when scatter enhancer is hidden!`);
                    }
                    const overlay = new EnhancerXNudgeOverlay(reelId, symId);
                    SerialLayers.getLayer(Layers.ENHANCER_NUDGE_OVERLAY)?.addChild(overlay);
                    this.enhancerOverlays[reelId] = overlay;
                    overlay.nudgeTo(ReelPositionController.REEL_AREAS[reelId].height - ReelPositionController.DEFAULT_SYMBOL_HEIGHT, 0.01, "none");
                    overlay.updateMultiplier(multiplier, false);
                    return;
                }
                if(symId === 2) {
                    // middle reel
                    const w = <SerialStackedWildSymbol>Serial.getSymbol(reelId, symId);
                    if(!w.updateMultiplier) {
                        throw new Error(`Nudge restore error on reelId ${reelId}, symId:${symId}`);
                    }
                    w.updateMultiplier(multiplier, false);
                    return;
                }

                debugger;
                throw new Error(`Nudge restore error on reelId ${reelId}, symId:${symId}`);
            });

        }
    }

    protected hasWin():boolean {
        const data = Serial.model.parsedGameData;

        if(data.newXNudge.some((reelValues, reelId) => {
            return reelValues.some((value, symId) => value);
        })) {
            this._hasWin = data.betWins.length > 0;
            return true;
        }
        return false;
    }

    protected async createWinAnimation():Promise<void> {
        const data = Serial.model.parsedGameData;
        let delay = 0;
        const promises:Promise<any>[] = [];
        data.newXNudge.forEach((doNudge, reelId) => {
            const symId = doNudge.indexOf(true);
            if(symId < 0) {
                return;
            }
            if(symId > 1 && symId < 5) {
                // middle reels normal nudge
                const promise = new Promise<void>(resolve => {
                    Serial.reels[reelId].nudge(
                        Serial.model.parsedGameData.nudgeSteps[reelId],
                        this._hasWin ? this._nudgeStepDuration : 0.01,
                        this._nudgeEase,
                        delay,
                        (stepsLeft:number, aborted:boolean) => this.onNudgeStepStart(reelId, stepsLeft, aborted),
                        (stepsLeft:number, aborted:boolean) => this.onNudgeStepComplete(reelId, stepsLeft, aborted),
                        async (aborted:boolean) => {
                            await this.onReelNudgeComplete(reelId, aborted);
                            resolve();
                        },
                        false
                    );
                });
                promises.push(promise);
            } else {
                // enhancer nudge
                promises.push(new Promise<void>(async resolve => {
                    await Promise.resolve(this.getEnhancerReelNudge(reelId, symId, delay));
                    await this.onEnhancerReelNudgeComplete(reelId);
                    resolve();
                }));
            }
            delay += this._hasWin ? this.getNextReelNudgeDelayDelta(reelId) : 0.01;
        });
        if(!this._hasWin && ArrayHelper.arraySum(Serial.model.parsedGameData.nudgeSteps) > 0) {
            SerialSound.soundPlayer.playTrack({name : SerialSound.NUDGE_NO_WIN});
        }
        await Promise.all(promises);
    }

    private getEnhancerReelNudge(reelId:number, symId:number, delay:number):gsap.core.Timeline {
        const tl = gsap.timeline();
        const stepCount = Serial.model.parsedGameData.nudgeSteps[reelId];
        const layer = SerialLayers.getLayer(Layers.ENHANCER_NUDGE_OVERLAY);
        const overlay = new EnhancerXNudgeOverlay(reelId, symId);
        this.enhancerOverlays[reelId] = overlay;
        let targetHeight = overlay.currentHeight;
        tl.add(() => {layer!.addChild(overlay);}, delay);
        for(let i = 0; i < Math.abs(stepCount); i++) {
            const targetSymId = stepCount > 0 ? (symId + 1 + i) : (symId - 1 - i);
            if(targetSymId === 1 || targetSymId === 5) {
                if(!Serial.enhancerReels[reelId][targetSymId].isOpened) {
                    continue;
                }
            }
            targetHeight += Serial.getSymbol(reelId, targetSymId).symbolHeight;
            tl.add(() => this.onEnhancerNudgeStepStart(reelId, overlay));
            tl.add(overlay.nudgeTo(targetHeight, this._hasWin ? this._nudgeStepDuration : 0.01, this._nudgeEase));
            tl.add(() => this.onEnhancerNudgeStepComplete(reelId));
        }
        this._enhancerNudgeAnimations[reelId] = tl;
        return tl;
    }

    protected skip() {
        this._skipped = true;
        let playAbortSound:boolean = false;
        Serial.reels.forEach(reel => {
            if(reel.reelState === ReelState.NUDGE) {
                reel.abortNudge();
                playAbortSound = true;
            }
        });
        this._enhancerNudgeAnimations.forEach((tl, reelId) => {
            if(tl) {
                if(tl.totalDuration() > 0 && tl.isActive()) {
                    tl.progress(1);
                    playAbortSound = true;
                }
                this._enhancerNudgeAnimations[reelId] = null;
            }
        });
        if(playAbortSound) {
            SerialSound.soundPlayer.playTrack({name : SerialSound.NUDGE_SKIP});
        }
    }

    protected getNextReelNudgeDelayDelta(reelId:number):number {
        return Math.abs(Serial.model.parsedGameData.nudgeSteps[reelId]) * this._nudgeStepDuration;
    }

    private onNudgeStepStart(reelId:number, stepsLeft:number, aborted:boolean):void {
        // Logger.logDev(`Reel ${reelId} start nudge step, stepsLeft = ${stepsLeft}, abort = ${aborted}`);

        const symId = stepsLeft > 0 ? 2 : 4;
        const w = <SerialStackedWildSymbol>Serial.getSymbol(reelId, symId);
        if(!w.updateMultiplier) {
            throw new Error(`Nudge error on reelId ${reelId}, symId:${symId}, stepsLeft:${stepsLeft}, aborted:${aborted}`)
        }
        const tl = gsap.timeline();
        tl.add([
            w.updateMultiplier(w.currentMultiplier + 1, true),
            () => {w.playNudge()}
        ]);
        if(!aborted && this._hasWin) {
            tl.add(() => {SerialSound.soundPlayer.playTrack({name : SerialSound.NUDGE})}, 0);
        }
        this._startStepAnimations[reelId] = tl;
    }

    private onNudgeStepComplete(reelId:number, stepsLeft:number, aborted:boolean):void {
        // Logger.logDev(`Reel ${reelId} completed nudge step, stepsLeft = ${stepsLeft}, abort = ${aborted}`);
    }

    private async onReelNudgeComplete(reelId:number, aborted:boolean):Promise<any> {
        // Logger.logDev(`Reel ${reelId} completed reel nudge, abort = ${aborted}`);
        const tl = this._startStepAnimations[reelId];
        if(tl && tl.totalDuration() > 0) {
            await Promise.resolve(tl);
        } else {
            await Promise.resolve();
        }
        this._startStepAnimations[reelId] = null;
        Logger.logDev(`Reel ${reelId} completed reel nudge, abort = ${aborted}`);
    }

    private onEnhancerNudgeStepStart(reelId:number, overlay:EnhancerXNudgeOverlay):void {
        // Logger.logDev(`Reel ${reelId} start nudge step, stepsLeft = ${stepsLeft}, abort = ${aborted}`);

        const tl = gsap.timeline();
        tl.add([
            overlay.updateMultiplier(overlay.currentMultiplier + 1, true),
            // () => {
            //     if(stepsLeft > 0) {
            //         w.playNudgeDown();
            //     } else {
            //         w.playNudgeUp();
            //     }
            // },
        ]);
        if(!this._skipped && this._hasWin) {
            tl.add(() => {SerialSound.soundPlayer.playTrack({name : SerialSound.NUDGE})}, 0);
        }
        this._startStepAnimations[reelId] = tl;
    }

    private onEnhancerNudgeStepComplete(reelId:number):void {
        // Logger.logDev(`Reel ${reelId} completed nudge step, stepsLeft = ${stepsLeft}, abort = ${aborted}`);
    }

    private async onEnhancerReelNudgeComplete(reelId:number):Promise<any> {
        // Logger.logDev(`Reel ${reelId} completed reel nudge, abort = ${aborted}`);
        const tl = this._startStepAnimations[reelId];
        if(tl && tl.totalDuration() > 0) {
            await Promise.resolve(tl);
        } else {
            await Promise.resolve();
        }
        this._startStepAnimations[reelId] = null;
        Logger.logDev(`Enhancer Reel ${reelId} completed reel nudge`);
    }

    public reset():void {
        this._skipped = false;
    }

    public resetReel(reelId:number):void {
        const overlay = this.enhancerOverlays[reelId];
        if(overlay) {
            gsap.to(overlay, {
                alpha : 0,
                duration : 0.2,
                onComplete : () => {
                    if(overlay.parent) {
                        overlay.parent.removeChild(overlay);
                    }
                    overlay.destroy();
                }
            });
            this.enhancerOverlays[reelId] = null;
        }
    }
}